import { Injectable, inject } from '@angular/core';
import { AuthService } from '../../services/auth.service';
import { RealtimeService } from './realtime.service';
import { RealtimeCacheBridge } from './realtime-cache-bridge.service';

/**
 * Démarre / arrête automatiquement le pont Realtime en suivant l'état de
 * connexion de l'utilisateur.
 *
 * <p>Singleton instancié au démarrage de l'app. S'abonne à
 * {@link AuthService#user$} et :</p>
 * <ul>
 *   <li>quand un user apparaît (login) → {@code realtime.start()} +
 *       {@code bridge.start()} ;</li>
 *   <li>quand le user disparaît (logout / expiration) → {@code realtime.stop()}.</li>
 * </ul>
 *
 * <p>Pas de cycle d'injection : ce service consomme tout, personne ne le
 * consomme. Il est tiré dans {@code app.config.ts} via APP_INITIALIZER.</p>
 */
@Injectable({ providedIn: 'root' })
export class RealtimeBootstrap {
  private auth = inject(AuthService);
  private realtime = inject(RealtimeService);
  private bridge = inject(RealtimeCacheBridge);

  private started = false;

  init(): void {
    this.auth.user$.subscribe(async (user) => {
      // Les admins Dinki ne sont pas liés à un atelier ; les tables suivies
      // par le bridge Realtime (client, mesure, commande…) sont des données
      // tenant qu'ils ne consomment pas. On évite donc d'ouvrir une
      // souscription WebSocket inutile et l'IndexedDB associée.
      const isAdmin = this.auth.hasRole('ADMIN');
      if (user && !isAdmin && !this.started) {
        try {
          await this.realtime.start();
          this.bridge.start();
          this.started = true;
        } catch {
          // Realtime non disponible (offline initial, backend down…) — silencieux.
          // Le HttpInterceptor offline gérera la suite.
        }
      } else if ((!user || isAdmin) && this.started) {
        this.realtime.stop();
        this.started = false;
      }
    });
  }
}
